import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Shield, QrCode, Trash2 } from "lucide-react";
import { useTr } from "@/i18n/translator";

export default function Security() {
  const tr = useTr();
  const [status, setStatus] = useState({ totp_enabled: false, pending_enrollment: false });
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [pwd, setPwd] = useState("");

  const refresh = async () => {
    const { data } = await api.get("/auth/2fa/status");
    setStatus(data);
  };

  useEffect(() => { refresh(); }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enroll");
      setEnroll(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar enrolamiento");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.length !== 6) return toast.error(tr("El código debe tener 6 dígitos"));
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("2FA activado");
      setEnroll(null);
      setCode("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Código inválido");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!pwd || code.length !== 6) return toast.error(tr("Completa contraseña y código"));
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: pwd, code });
      toast.success("2FA desactivado");
      setPwd(""); setCode("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="security-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("DOSSIER · SEGURIDAD · 2FA")}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">{tr("Autenticación de 2 factores")}</h1>
        <p className="mt-3 font-mono-data text-sm text-[#5C5C5C] max-w-xl">
          {tr("Protege tu cuenta con TOTP. Funciona con")} <b>Google Authenticator</b>, <b>Authy</b>, <b>1Password</b> {tr("y cualquier app compatible.")}
        </p>
      </div>

      {/* Status card */}
      <div className="bg-white border border-[#0A0A0A] p-6" data-testid="2fa-status">
        <div className="flex items-center gap-3">
          {status.totp_enabled ? (
            <><ShieldCheck className="w-8 h-8 text-[#E53935]" /><div>
              <div className="font-display text-2xl">{tr("2FA · ACTIVO")}</div>
              <div className="font-mono-data text-sm text-[#5C5C5C]">{tr("Tu cuenta está protegida.")}</div>
            </div></>
          ) : (
            <><Shield className="w-8 h-8 text-[#5C5C5C]" /><div>
              <div className="font-display text-2xl">{tr("2FA · DESACTIVADO")}</div>
              <div className="font-mono-data text-sm text-[#5C5C5C]">{tr("Actívalo para proteger tu cuenta.")}</div>
            </div></>
          )}
        </div>
      </div>

      {/* Enroll flow */}
      {!status.totp_enabled && !enroll && (
        <button
          onClick={startEnroll}
          disabled={busy}
          className="bg-[#0A0A0A] text-white px-6 py-3 font-mono-mark text-xs hover-brutal border border-[#0A0A0A] flex items-center gap-2 disabled:opacity-50"
          data-testid="2fa-enroll-start"
        >
          <QrCode className="w-4 h-4" /> {busy ? tr("INICIANDO…") : tr("ACTIVAR 2FA")}
        </button>
      )}

      {enroll && (
        <div className="bg-white border border-[#0A0A0A] p-6 space-y-5" data-testid="2fa-enroll">
          <div>
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("PASO 1 · ESCANEA ESTE CÓDIGO")}</div>
            <div className="mt-3 bg-white border border-[#0A0A0A] p-4 inline-block">
              <img src={enroll.qr_data_url} alt="QR 2FA" className="w-48 h-48" />
            </div>
            <div className="mt-3 font-mono-data text-xs text-[#5C5C5C]">
              {tr("O ingresa esta clave manualmente:")} <code className="bg-[#F4F4F0] px-2 py-1 ml-2 select-all">{enroll.secret}</code>
            </div>
          </div>
          <div>
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("PASO 2 · INGRESA EL CÓDIGO DE 6 DÍGITOS")}</div>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-lg tracking-[0.5em] text-center w-48"
              placeholder="000000"
              data-testid="2fa-code-input"
            />
          </div>
          <button onClick={verify} disabled={busy} className="bg-[#E53935] text-white px-6 py-3 font-mono-mark text-xs hover-brutal border border-[#E53935] disabled:opacity-50" data-testid="2fa-verify">
            {busy ? "VERIFICANDO…" : "VERIFICAR Y ACTIVAR"}
          </button>
        </div>
      )}

      {/* Disable flow */}
      {status.totp_enabled && (
        <div className="bg-white border border-[#E53935] p-6 space-y-4" data-testid="2fa-disable">
          <div className="font-mono-mark text-[10px] text-[#E53935]">{tr("ZONA DE RIESGO · DESACTIVAR 2FA")}</div>
          <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
            placeholder={tr("Contraseña actual")}
            className="w-full bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm"
            data-testid="2fa-disable-pwd" autoComplete="current-password" />
          <input type="text" inputMode="numeric" maxLength={6} value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder={tr("Código 2FA actual · 6 dígitos")}
            className="w-full bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm"
            data-testid="2fa-disable-code" />
          <button onClick={disable} disabled={busy}
            className="bg-[#E53935] text-white px-6 py-3 font-mono-mark text-xs border border-[#E53935] disabled:opacity-50 flex items-center gap-2"
            data-testid="2fa-disable-submit">
            <Trash2 className="w-4 h-4" /> {tr("DESACTIVAR 2FA")}
          </button>
        </div>
      )}
    </div>
  );
}
